import { useState, useEffect, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { trackLogin, trackSignUp, type AuthMethod } from "@/lib/analytics-enhanced";
import { saveAccountAcquisition } from "@/lib/account-acquisition";
import {
  GUEST_SIGNUP_COUNTED,
  authMomentKey,
  claimAuthMoment,
  classifyAuthMoment,
  isGuestCheckoutAccount,
  type AuthMoment,
} from "@/lib/auth-moment";
import { clearUserIdentity, identifyUser, markInternalTraffic } from "@/lib/analytics-identity";
import { logUsage, setUsageAccessToken } from "@/lib/usage-events";
import { resolvePlan } from "@/lib/entitlements";
import { forgetStatewideSearches } from "@/lib/statewide-memory";

export interface Profile {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  subscription_tier: "free" | "monthly" | "yearly" | "lifetime";
  subscription_status: "active" | "cancelling" | "cancelled" | "past_due" | "expired";
  subscription_start_at: string | null;
  subscription_end_at: string | null;
  manual_premium: boolean;
  /** Set once checkout creates a Stripe customer; `describeAccountPlan` reads it. */
  stripe_customer_id?: string | null;
  marketing_opt_in: boolean;
  created_at: string;
  updated_at: string;
}

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  /**
   * The signed-in user's profile read has answered at least once, with a row
   * or with none. A failed read does not count: the plan is still unknown.
   * `profile === null` alone cannot tell "still loading" from "no profile",
   * and a paying user's link must not be decoded as free in between.
   */
  profileSettled: boolean;
  loading: boolean;
  isPremium: boolean;
  signUp: (email: string, password: string, metadata?: { marketing_opt_in?: boolean }, redirectPath?: string) => Promise<{ error: Error | null; accessToken?: string; alreadyRegistered?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  checkSubscription: () => Promise<void>;
}

/**
 * GA4 `sign_up` / `login` plus the usage-log row. Called once per real sign-in
 * — `classifyAuthMoment` decides which, `claimAuthMoment` makes it once.
 */
function reportAuthMoment(user: User, moment: AuthMoment) {
  const method: AuthMethod = user.app_metadata?.provider === "google" ? "google" : "password";

  if (moment === "sign_up") {
    trackSignUp(method);
    logUsage("signed_in", { meta: { new_account: true, method } });
  } else {
    trackLogin(method);
    logUsage("signed_in", { meta: { new_account: false, method } });
  }
}

/**
 * Where an auth email or OAuth round trip should land: this origin plus the
 * page the person was headed to. Only a same-origin path is honored — a
 * `redirect` query value is whatever the link said, and `origin + "@evil.com"`
 * would name another host.
 *
 * The path matters most for the OAuth consent screen: its
 * `/oauth/consent?authorization_id=…` *is* the connection request, and a
 * confirmation link that returns to `/` in a new tab (where the sessionStorage
 * copy does not exist) loses it.
 */
export function authReturnUrl(redirectPath?: string): string {
  const path = redirectPath && /^\/(?![/\\])/.test(redirectPath) ? redirectPath : "/";
  return `${window.location.origin}${path}`;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  // The user id whose profile fetch last finished (any outcome).
  const [profileSettledFor, setProfileSettledFor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const subscriptionCheckInterval = useRef<NodeJS.Timeout | null>(null);
  // Latest session, read by the stable checkSubscription callback below so it
  // doesn't change identity on every auth event (which caused duplicate
  // check-subscription + profile requests on each page load).
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        return;
      }

      setProfile(data as Profile | null);
      // Only an answer settles the plan. A failed read is not "no profile":
      // read as free, it stripped a paying user's link filters on a blip.
      setProfileSettledFor(userId);
    } catch (err) {
      console.error("Error fetching profile:", err);
    }
  }, []);

  // Check subscription status from Stripe. Stable identity (reads the session
  // from a ref) so the effect below only fires when the token actually changes.
  const checkSubscription = useCallback(async () => {
    const currentSession = sessionRef.current;
    if (!currentSession?.access_token) return;

    try {
      // No Authorization header: invoke attaches the current session's token,
      // refreshed first. The ref's copy is expired after a tab sleeps, until
      // supabase-js raises TOKEN_REFRESHED, and this runs on an interval.
      const { data, error } = await supabase.functions.invoke("check-subscription");

      if (error) {
        console.error("Error checking subscription:", error);
        return;
      }

      // If subscription data changed, refresh profile
      if (data && currentSession.user) {
        await fetchProfile(currentSession.user.id);
      }
    } catch (err) {
      console.error("Error checking subscription:", err);
    }
  }, [fetchProfile]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Synchronously, before anything below can log: the usage log stamps
      // each event with the token current *when it happened*, so the
      // `signed_in` row must already carry the new one. Every event, not just
      // SIGNED_IN — TOKEN_REFRESHED is what keeps a long session attributed.
      setUsageAccessToken(session?.access_token ?? null);
      setSession(session);
      setUser(session?.user ?? null);

      // Defer profile fetch with setTimeout to avoid deadlock
      if (session?.user) {
        setTimeout(() => {
          fetchProfile(session.user.id);
        }, 0);
      } else {
        setProfile(null);
      }

      // Report sign_up / login from here rather than from signUp()/signIn(),
      // for two reasons: OAuth never returns to its caller (it round-trips
      // through Google), and a password signUp *also* raises SIGNED_IN, so
      // instrumenting both would double-count every account.
      if (event === "SIGNED_IN" && session?.user) {
        // Identify *before* reporting, so `sign_up` / `login` themselves carry
        // the user id. The effect below keeps it in step afterwards, but it
        // cannot run until React re-renders — by which point these two events
        // would already have gone out anonymous.
        identifyUser(session.user.id);

        // Most SIGNED_IN events are a restored session, a refocused tab or
        // another tab's broadcast, not a sign-in. See auth-moment.ts.
        const moment = classifyAuthMoment(session.user);
        if (moment) {
          const signedIn = session.user;
          const accessToken = session.access_token;
          void claimAuthMoment(authMomentKey(signedIn)).then((claimed) => {
            if (!claimed) return;
            reportAuthMoment(signedIn, moment);

            // Where the new account came from. Deferred like the rest: a
            // PostgREST call from inside this callback can deadlock the client.
            if (moment === "sign_up") setTimeout(() => void saveAccountAcquisition(), 0);

            // A guest buyer's account: mark its first sign-in counted so the
            // next one is a login. No welcome email — it pitches Free and Lite
            // to someone who has just bought Lite, and the webhook already sent
            // them the Lite one. Deferred like fetchProfile: an auth call made
            // from inside this callback can deadlock the client.
            if (moment === "sign_up" && isGuestCheckoutAccount(signedIn)) {
              setTimeout(() => {
                void supabase.auth.updateUser({ data: { [GUEST_SIGNUP_COUNTED]: true } });
              }, 0);
            } else if (moment === "sign_up" && accessToken) {
              // The welcome email rides the same claim, so a second open tab
              // can no longer send it twice.
              supabase.functions.invoke("send-welcome-email", {
                headers: { Authorization: `Bearer ${accessToken}` },
              }).catch((err) => console.error("Welcome email failed:", err));
            }
          });
        }
      }

      if (event === "SIGNED_OUT") {
        clearUserIdentity();
        // Statewide results kept in this tab's memory hold owner names.
        forgetStatewideSearches();
        setProfile(null);
        // Clear subscription check interval
        if (subscriptionCheckInterval.current) {
          clearInterval(subscriptionCheckInterval.current);
          subscriptionCheckInterval.current = null;
        }
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUsageAccessToken(session?.access_token ?? null);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (subscriptionCheckInterval.current) {
        clearInterval(subscriptionCheckInterval.current);
      }
    };
  }, [fetchProfile]);

  // Keep GA4 and Clarity's idea of who this is in step with the session.
  //
  // The SIGNED_IN handler above covers the sign-in moment; this covers the two
  // cases it cannot. A returning visitor restores their session through
  // `getSession()` without a SIGNED_IN event, and the `plan` user property
  // can only be set once the profile row has arrived — which is always after
  // the session does, and again after every `checkSubscription` refresh, so a
  // customer who upgrades mid-session is re-stamped without a reload.
  //
  // Gated on `loading` so a returning subscriber is never briefly reported as
  // signed out while `getSession()` is still in flight.
  useEffect(() => {
    if (loading) return;

    if (user) {
      identifyUser(user.id, profile ? resolvePlan(profile) : undefined);
    } else {
      clearUserIdentity();
    }
  }, [loading, user, profile]);

  // Internal accounts (the owner, test accounts) tag their GA4 and Clarity
  // traffic so reports can leave it out. The row is readable only by the
  // account itself, and only whether it exists. One tiny query per account per
  // page; a failure just leaves the traffic untagged.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void supabase
      .from("analytics_internal_users")
      .select("user_id")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (data && !cancelled) markInternalTraffic();
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Check subscription on session change and periodically
  useEffect(() => {
    if (session?.access_token) {
      // Check immediately
      checkSubscription();

      // Set up periodic check (every 5 minutes)
      subscriptionCheckInterval.current = setInterval(() => {
        checkSubscription();
      }, 300000);
    }

    return () => {
      if (subscriptionCheckInterval.current) {
        clearInterval(subscriptionCheckInterval.current);
        subscriptionCheckInterval.current = null;
      }
    };
  }, [session?.access_token, checkSubscription]);

  const signUp = useCallback(async (email: string, password: string, metadata?: { marketing_opt_in?: boolean }, redirectPath?: string) => {
    const redirectUrl = authReturnUrl(redirectPath);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          marketing_opt_in: metadata?.marketing_opt_in ?? true,
        },
      },
    });

    return {
      error: error ? new Error(error.message) : null,
      // No session means the confirmation email is the next step.
      accessToken: data.session?.access_token,
      // Supabase's anti-enumeration answer for an address that already has an
      // account: a success, a user with no identities, and no email sent.
      alreadyRegistered: !error && !data.session && data.user?.identities?.length === 0,
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error ? new Error(error.message) : null };
  }, []);

  const signInWithGoogle = useCallback(async (redirectPath?: string) => {
    const redirectTo = authReturnUrl(redirectPath);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
      },
    });

    return { error: error ? new Error(error.message) : null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  // Date-based: `subscription_end_at` is "paid through" and status is
  // informational. Derived from the same helper the feature gate uses so the
  // two can't disagree. See resolvePlan for why status no longer participates.
  const isPremium = resolvePlan(profile) !== "free";

  return {
    user,
    session,
    profile,
    profileSettled: !!user && profileSettledFor === user.id,
    loading,
    isPremium,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    refreshProfile,
    checkSubscription,
  };
}
